import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HeadingBlock } from './blocks/HeadingBlock';
import { ParagraphBlock } from './blocks/ParagraphBlock';
import { ImageBlock } from './blocks/ImageBlock';
import { VideoBlock } from './blocks/VideoBlock';
import {} from './interpolate';
import { evaluateVisibility } from './visibility';
/**
 * The visible, non-input blocks of a card.
 *
 * Visibility resolves PER RECIPIENT against the live fact map, which is why a
 * card can be non-empty at publish time and still render blank for one
 * recipient — the defect class the publish gate structurally cannot catch
 * (ENG-177, and the reason iOS added the ENG-229 breadcrumb). Exported so
 * callers can detect an all-hidden card instead of silently showing nothing.
 */
export function visibleBlocks(blocks, facts) {
    return blocks
        .filter((b) => b.type !== 'input')
        .filter((b) => evaluateVisibility(b.visible_when, facts));
}
/** Rich authored text, rendered through the one rich-text block component. */
function RichBody({ content, vars, className, }) {
    const asParagraph = {
        id: 'rich',
        type: 'paragraph',
        content: content ?? '',
        textAlign: 'left',
    };
    return _jsx(ParagraphBlock, { block: asParagraph, vars: vars, className: className });
}
export function BlockView({ block, vars, }) {
    switch (block.type) {
        case 'heading':
            return _jsx(HeadingBlock, { block: block, vars: vars });
        case 'paragraph':
            return _jsx(ParagraphBlock, { block: block, vars: vars });
        case 'image':
            return _jsx(ImageBlock, { block: block });
        case 'video':
            return _jsx(VideoBlock, { block: block });
        case 'input':
            // Inputs render via the dedicated InputBlock at the bottom of the card,
            // not here.
            return null;
        case 'divider':
            return _jsx("hr", { className: "border-slate-200 my-1" });
        case 'spacer': {
            const height = block.size === 'sm' ? 12 : block.size === 'lg' ? 40 : 24;
            return _jsx("div", { style: { height }, "aria-hidden": "true" });
        }
        case 'callout':
            return _jsx(CalloutBlockView, { block: block, vars: vars });
        case 'button':
            return _jsx(ButtonBlockView, { block: block });
        case 'audio':
            return _jsx(AudioBlockView, { block: block });
        case 'embed':
            return _jsx(EmbedBlockView, { block: block });
        case 'accordion':
            return _jsx(AccordionBlockView, { block: block, vars: vars });
        default: {
            // Exhaustiveness guard. Adding a member to the `Block` union without a
            // case above is now a COMPILE error here, not a block that silently
            // vanishes from the player. The warn covers the other direction —
            // content authored against a newer schema than the deployed player.
            const exhaustive = block;
            console.warn('[tutorial-player] unknown block type, skipping:', exhaustive.type);
            return null;
        }
    }
}
function ButtonBlockView({ block }) {
    // Buttons render as inert previews; the host app's onComplete/onCardChange
    // wiring handles the action. Note the publish gate REJECTS button blocks in
    // graph workflows (D6) precisely because they bypass the edge model — this
    // path only renders for legacy v2 content.
    const variantClass = {
        primary: 'bg-teal-500 text-white border-teal-500 shadow-sm',
        secondary: 'bg-white text-teal-700 border-teal-300',
        subtle: 'bg-transparent text-slate-600 border-transparent underline',
    }[block.variant];
    return (_jsx("button", { type: "button", className: `w-full px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${variantClass}`, children: block.label || 'Continue' }));
}
function AudioBlockView({ block }) {
    return (_jsxs("div", { className: "rounded-xl border border-slate-200 bg-slate-50 p-3", children: [block.media_url ? (
            // Narration audio has no caption track in the authoring model; the
            // author-supplied `transcript` below is the accessible equivalent.
            _jsx("audio", { src: block.media_url, controls: true, autoPlay: block.autoplay, className: "w-full" })) : (_jsx("p", { className: "text-xs text-slate-400 italic", children: "No audio yet." })), block.transcript && (_jsxs("details", { className: "mt-2", children: [_jsx("summary", { className: "text-xs font-medium text-slate-600 cursor-pointer hover:text-slate-800", children: "Transcript" }), _jsx("p", { className: "mt-1.5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed", children: block.transcript })] }))] }));
}
function EmbedBlockView({ block }) {
    const embedUrl = resolveEmbedUrl(block.provider, block.url);
    const padding = block.aspect === 'square' ? '100%' : block.aspect === 'tall' ? '125%' : '56.25%';
    return (_jsx("div", { className: "w-full rounded-xl overflow-hidden bg-slate-900 relative", style: { paddingBottom: padding }, children: embedUrl ? (_jsx("iframe", { src: embedUrl, title: "Embedded media", allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share", allowFullScreen: true, className: "absolute inset-0 w-full h-full" })) : (_jsxs("div", { className: "absolute inset-0 flex items-center justify-center text-slate-400 text-xs", children: ["Paste a ", block.provider, " URL to preview"] })) }));
}
function AccordionBlockView({ block, vars, }) {
    return (_jsxs("details", { open: block.default_open, className: "rounded-xl border border-slate-200 bg-white overflow-hidden group", children: [_jsxs("summary", { className: "flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 transition-colors list-none", children: [_jsx("span", { children: block.title || 'More info' }), _jsx("span", { className: "text-slate-400 group-open:rotate-180 transition-transform", children: "\u25BE" })] }), _jsx(RichBody, { content: block.content ?? '', vars: vars, className: "tutorial-prose px-4 py-3 text-sm leading-relaxed text-slate-700 border-t border-slate-100" })] }));
}
/** Best-effort extraction of an embed URL from common share URLs. */
function resolveEmbedUrl(provider, url) {
    if (!url)
        return '';
    try {
        const parsed = new URL(url);
        if (provider === 'youtube') {
            const v = parsed.searchParams.get('v');
            if (v)
                return `https://www.youtube.com/embed/${v}`;
            if (parsed.hostname.includes('youtu.be')) {
                const id = parsed.pathname.replace(/^\//, '');
                return `https://www.youtube.com/embed/${id}`;
            }
            return url;
        }
        if (provider === 'vimeo') {
            const m = parsed.pathname.match(/(\d+)/);
            if (m)
                return `https://player.vimeo.com/video/${m[1]}`;
            return url;
        }
        if (provider === 'loom') {
            return url.replace('/share/', '/embed/');
        }
        return url;
    }
    catch {
        return '';
    }
}
function CalloutBlockView({ block, vars, }) {
    const palette = {
        info: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', accent: 'text-blue-500' },
        warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-900', accent: 'text-amber-500' },
        success: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', accent: 'text-green-600' },
        tip: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-900', accent: 'text-violet-500' },
    }[block.variant];
    const emoji = { info: 'ℹ️', warning: '⚠️', success: '✓', tip: '💡' }[block.variant];
    return (_jsxs("div", { className: `flex items-start gap-3 rounded-xl border ${palette.bg} ${palette.border} px-4 py-3`, children: [_jsx("span", { className: `text-xl leading-none flex-shrink-0 ${palette.accent}`, "aria-hidden": "true", children: emoji }), _jsxs("div", { className: `min-w-0 flex-1 ${palette.text}`, children: [block.title && _jsx("div", { className: "text-sm font-semibold mb-1", children: block.title }), _jsx(RichBody, { content: block.content ?? '', vars: vars, className: "tutorial-prose text-sm leading-relaxed" })] })] }));
}
/**
 * The end screen.
 *
 * Decoding is TOLERANT of unknown codes (ENG-160): an outcome this client does
 * not recognise still renders using whatever label travelled with the snapshot,
 * rather than crashing or showing nothing.
 */
export function OutcomeScreen({ code, fallbackTitle, outcomes, }) {
    const outcome = code ? outcomes[code] : undefined;
    const kind = outcome?.kind ?? 'success';
    const emoji = outcome?.emoji ?? (kind === 'failure' ? '😞' : kind === 'neutral' ? '✨' : '🎉');
    const headline = outcome?.label ??
        fallbackTitle ??
        (kind === 'failure' ? 'That was a tough one.' : 'Great work today.');
    const message = outcome?.display_message ??
        (kind === 'failure'
            ? "It's okay — you can come back to this anytime."
            : 'Thanks for taking the time to learn this.');
    const accent = outcome?.color_hex ??
        (kind === 'failure' ? '#e11d48' : kind === 'neutral' ? '#64748b' : '#0d9488');
    return (_jsxs("div", { className: "flex flex-col items-center gap-4 rounded-3xl px-6 py-12 text-center", style: { backgroundColor: accent + '14' }, children: [_jsx("div", { className: "text-6xl leading-none", "aria-hidden": "true", children: emoji }), _jsx("p", { className: "text-2xl font-semibold", style: { color: accent }, children: headline }), _jsx("p", { className: "max-w-sm text-sm text-slate-600", children: message })] }));
}
export function EmptyState({ message }) {
    return (_jsx("div", { className: "flex flex-col items-center justify-center gap-2 rounded-2xl bg-slate-50 px-6 py-12 text-center text-sm text-slate-500", children: message }));
}
/**
 * A loud, non-silent failure surface.
 *
 * The point of the graph model is that "no branch matched" can no longer end a
 * tutorial quietly. When `advance()` returns `status: 'error'` the learner sees
 * this instead of a blank screen or a fabricated success.
 */
export function PlayerErrorState({ message }) {
    return (_jsxs("div", { className: "flex flex-col items-center justify-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-6 py-10 text-center", children: [_jsx("div", { className: "text-3xl leading-none", "aria-hidden": "true", children: "\u26A0\uFE0F" }), _jsx("p", { className: "text-sm font-semibold text-amber-900", children: "This tutorial can\u2019t continue." }), _jsx("p", { className: "max-w-sm text-xs text-amber-800", children: message })] }));
}
